import React, { useEffect, useMemo, useState } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { DeviceLinkModel } from 'src/Libs/installations'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import { UnitTypes } from 'src/Libs/types'
import { BlankView, BlankViewImageVariants, BlankViewImageSizes } from 'src/Components/blank-view'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { useLimitPower } from 'src/Libs/installations/hooks/installation-configuration/em360/use-limit-power'
import { EM360_DEFAULT_LIMIT_POWER_SETTINGS } from 'src/Libs/installations/constants'

interface Props {
  installationId?: number
  emcDeviceLink?: DeviceLinkModel
  isEmcConnectionLoading: boolean
}

const schema = z.object({
  powerLimitsUsed: z.boolean(),
  emsMaxChargePower: z.coerce.number().min(0).max(Number.MAX_SAFE_INTEGER),
  emsMaxDischargePower: z.coerce.number().min(0).max(Number.MAX_SAFE_INTEGER),
  frequencyReverseProtectionLower: z.coerce.number().min(0).max(Number.MAX_SAFE_INTEGER),
})

type LimitPowerFormValues = z.infer<typeof schema>

const LimitPowerPanel: React.FC<Props> = ({ installationId, emcDeviceLink, isEmcConnectionLoading }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const deviceLinkId = emcDeviceLink?.id?.toString() ?? null

  const {
    query: { data: storageConfig, isLoading: isConfigLoading, isError: isConfigError, refetch: refetchConfig },
    mutation: { mutate: saveConfig, isPending: isSaving },
  } = useLimitPower(installationId, deviceLinkId)

  const resolver = useMemo(
    () =>
      zodResolver(
        schema.refine(values => values.frequencyReverseProtectionLower <= values.emsMaxChargePower, {
          message: t('installation-configuration.em360.limit-power.validation.frequency-reverse-protection-lower-max'),
          path: ['frequencyReverseProtectionLower'],
        })
      ),
    [t]
  )

  // keeps the form in sync with the async-loaded config instead of manually reset()-ing in an effect,
  // which raced with useWatch subscribers when the query already had cached data on remount
  const formValues = useMemo(
    () =>
      storageConfig
        ? {
            powerLimitsUsed: storageConfig.powerLimitsUsed ?? EM360_DEFAULT_LIMIT_POWER_SETTINGS.powerLimitsUsed,
            emsMaxChargePower: storageConfig.emsMaxChargePower ?? EM360_DEFAULT_LIMIT_POWER_SETTINGS.emsMaxChargePower,
            emsMaxDischargePower: storageConfig.emsMaxDischargePower ?? EM360_DEFAULT_LIMIT_POWER_SETTINGS.emsMaxDischargePower,
            frequencyReverseProtectionLower:
              storageConfig.frequencyReverseProtectionLower ?? EM360_DEFAULT_LIMIT_POWER_SETTINGS.frequencyReverseProtectionLower,
          }
        : undefined,
    [storageConfig]
  )

  const methods = useForm<LimitPowerFormValues>({
    defaultValues: {
      ...EM360_DEFAULT_LIMIT_POWER_SETTINGS,
    },
    values: formValues,
    resolver,
    mode: 'onChange',
  })

  const [powerLimitsUsed, setPowerLimitsUsed] = useState(formValues?.powerLimitsUsed ?? false)

  useEffect(() => {
    if (formValues) {
      setPowerLimitsUsed(formValues.powerLimitsUsed)
      methods.trigger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues])

  const isLoading = isEmcConnectionLoading || isConfigLoading
  const isDisabled = !installationId || !deviceLinkId || isLoading || isSaving

  const handlePowerLimitsToggle = (newValue: boolean): void => {
    if (!newValue) {
      const lastValidValues = formValues ?? methods.formState.defaultValues
      methods.setValue('emsMaxChargePower', lastValidValues?.emsMaxChargePower ?? 0)
      methods.setValue('emsMaxDischargePower', lastValidValues?.emsMaxDischargePower ?? 0)
      methods.setValue('frequencyReverseProtectionLower', lastValidValues?.frequencyReverseProtectionLower ?? 0)
    }
    methods.setValue('powerLimitsUsed', newValue, { shouldDirty: true, shouldTouch: true })
    methods.trigger()
    setPowerLimitsUsed(newValue)
  }

  const onSubmit = (values: LimitPowerFormValues): void => {
    if (isDisabled) return
    saveConfig(values, {
      onSuccess: () => enqueueSuccessStack(t('installation-configuration.em360.limit-power.save.success')),
      onError: () => enqueueErrorStack(t('installation-configuration.em360.limit-power.save.error')),
    })
  }

  const renderContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      )
    }
    if (isConfigError) {
      return (
        <Box sx={{ py: 4 }}>
          <BlankView
            imageVariant={BlankViewImageVariants.Worried}
            imageSize={BlankViewImageSizes.Small}
            title={'installation-configuration.em360.limit-power.load.error'}
            action={{ text: 'common.reload', onClick: refetchConfig }}
          />
        </Box>
      )
    }
    return (
      <Stack gap={2}>
        <PowerLimitToggle disabled={isDisabled} powerLimitsUsed={powerLimitsUsed} onToggle={handlePowerLimitsToggle} />
        {powerLimitsUsed && <LimitPowerFields disabled={isDisabled} />}
      </Stack>
    )
  }

  return (
    <AccordionItem title={t('installation-configuration.em360.limit-power.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <FormProvider {...methods}>
        <Box component={'form'} onSubmit={methods.handleSubmit(onSubmit)}>
          {renderContent()}
          <Divider sx={{ marginX: -2, borderColor: 'grey.grey06', marginY: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type={'submit'} variant={'contained'} disabled={isDisabled || !methods.formState.isValid}>
              {t('common.save')}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </AccordionItem>
  )
}

//#region PowerLimitToggle
const PowerLimitToggle: React.FC<{ disabled: boolean; powerLimitsUsed: boolean; onToggle: (value: boolean) => void }> = ({
  disabled,
  powerLimitsUsed,
  onToggle,
}) => {
  const { t } = useCustomTranslation()

  // the toggle's visual state must be the inverse of the stored powerLimitsUsed value
  const selectedTabIndex = powerLimitsUsed ? 1 : 0

  const handleToggle = (toggledOn: boolean): void => {
    onToggle(!toggledOn)
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
      <Typography variant={'body2'}>{t('installation-configuration.em360.limit-power.auto-config')}</Typography>
      <Box sx={{ width: { xs: '100%', md: 280 }, borderRadius: theme => theme.shape.customBorderRadius.tabButton }}>
        <TabButtonGroupBase tabIndex={selectedTabIndex} disabled={disabled} styledContainerSx={{ backgroundColor: 'shades.shade04' }}>
          <TabButton onClick={() => handleToggle(true)}>{t('installation-configuration.em360.limit-power.enabled')}</TabButton>
          <TabButton onClick={() => handleToggle(false)}>{t('installation-configuration.em360.limit-power.disabled')}</TabButton>
        </TabButtonGroupBase>
      </Box>
    </Stack>
  )
}
//#endregion

//#region LimitPowerFields
type LimitPowerNumberFieldName = 'emsMaxChargePower' | 'emsMaxDischargePower' | 'frequencyReverseProtectionLower'

const LimitPowerNumberFieldRow: React.FC<{ name: LimitPowerNumberFieldName; label: string; disabled: boolean }> = ({ name, label, disabled }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
    <Typography variant={'body2'}>{label}</Typography>
    <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 50%' } }}>
      <NumberField
        name={name}
        label={label}
        disabled={disabled}
        fullWidth
        inputProps={{ min: 0, max: Number.MAX_SAFE_INTEGER }}
        suffix={UnitTypes.kiloWatt}
        isDecimal
        precision={2}
        step={0.01}
      />
    </Box>
  </Stack>
)

const LimitPowerFields: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const { t } = useCustomTranslation()

  return (
    // SettingsBox's root container has a fixed marginBottom: 12 (see SettingsBox.tsx) that cannot be
    // overridden via its own sx prop; this wrapper removes it here without touching the shared component.
    <Box sx={{ '& > div': { marginBottom: 0 } }}>
      <SettingsBox title={''}>
        <Stack gap={2} sx={{ mt: 1 }}>
          <LimitPowerNumberFieldRow
            name={'emsMaxChargePower'}
            label={t('installation-configuration.em360.limit-power.max-charge')}
            disabled={disabled}
          />
          <LimitPowerNumberFieldRow
            name={'emsMaxDischargePower'}
            label={t('installation-configuration.em360.limit-power.max-discharge')}
            disabled={disabled}
          />
          <LimitPowerNumberFieldRow
            name={'frequencyReverseProtectionLower'}
            label={t('installation-configuration.em360.limit-power.lower-loading-sill')}
            disabled={disabled}
          />
        </Stack>
      </SettingsBox>
    </Box>
  )
}
//#endregion

export default LimitPowerPanel
